import { StateCreator } from 'zustand';

import {
  addDrafts,
  addPending,
  appendHistory,
  applyServerMessageToState,
  cancelHighlightTimer,
  clearDrafts,
  clearSessionOutput,
  flashHighlight,
  flushSessionBuffer,
  markSuggestionDismissed,
  removeDraft,
  removePending,
  removeSessionFromState,
  renameSessionInState,
  setPending,
  setProgress,
} from './handlers';
import { TerminalState, TerminalStore } from './types';
import { TerminalConnectionStatus } from '../../enums/terminal';
import { TERMINAL_DEFAULT_HEIGHT_PX } from '../../utils/constants/terminalConstants';

export const INITIAL_TERMINAL_STATE: TerminalState = {
  isOpen: false,
  isMaximized: false,
  hasOpened: false,
  heightPx: TERMINAL_DEFAULT_HEIGHT_PX,
  focusNonce: 0,
  connection: TerminalConnectionStatus.CONNECTING,
  transportKind: null,
  activeDeviceId: null,
  isLoadingSessions: true,
  isPickingFolder: false,
  moveDialog: null,
  recentlyMovedTaskId: null,
  isAwaitingOpen: false,
  activeSessionId: null,
  sessionOrder: [],
  sessions: {},
  folders: [],
};

const DEVICE_SESSIONS_RESET: Partial<TerminalState> = {
  sessions: {},
  sessionOrder: [],
  activeSessionId: null,
  folders: [],
  isAwaitingOpen: false,
  moveDialog: null,
  isLoadingSessions: true,
  connection: TerminalConnectionStatus.CONNECTING,
};

const openState = (state: TerminalState): Partial<TerminalState> => ({
  isOpen: true,
  hasOpened: true,
  focusNonce: state.focusNonce + 1,
});

export const createTerminalSlice: StateCreator<TerminalStore> = (set) => ({
  ...INITIAL_TERMINAL_STATE,

  openDrawer: () => set(openState),
  hideDrawer: () => set({ isOpen: false, isMaximized: false }),
  toggleDrawer: () =>
    set((state) =>
      state.isOpen ? { isOpen: false, isMaximized: false } : openState(state),
    ),
  toggleMaximized: () => set((state) => ({ isMaximized: !state.isMaximized })),
  requestInputFocus: () =>
    set((state) => ({ focusNonce: state.focusNonce + 1 })),
  setHeight: (heightPx) => set({ heightPx }),
  setConnection: (connection) => set({ connection }),
  setTransportKind: (transportKind) => set({ transportKind }),
  setActiveDevice: (activeDeviceId) => set({ activeDeviceId }),
  resetDeviceSessions: () => set(DEVICE_SESSIONS_RESET),
  setActiveSession: (sessionId) =>
    set((state) =>
      sessionId in state.sessions && sessionId !== state.activeSessionId
        ? { activeSessionId: sessionId, focusNonce: state.focusNonce + 1 }
        : {},
    ),
  markOpenRequested: () => set({ isAwaitingOpen: true }),
  finishLoadingSessions: () => set({ isLoadingSessions: false }),
  flushReorderBuffer: (sessionId) =>
    set((state) => flushSessionBuffer(state, sessionId)),
  openMoveDialog: (sessionId, mode) => set({ moveDialog: { sessionId, mode } }),
  closeMoveDialog: () => set({ moveDialog: null }),
  flashRecentlyMovedTask: (taskId) =>
    flashHighlight(
      (recentlyMovedTaskId) => set({ recentlyMovedTaskId }),
      taskId,
    ),
  focusSession: (sessionId) =>
    set((state) => ({
      ...openState(state),
      activeSessionId:
        sessionId in state.sessions ? sessionId : state.activeSessionId,
    })),
  setPickingFolder: (isPickingFolder) => set({ isPickingFolder }),
  addDraftAttachments: (sessionId, files) =>
    set((state) => addDrafts(state, sessionId, files)),
  removeDraftAttachment: (sessionId, id) =>
    set((state) => removeDraft(state, sessionId, id)),
  clearDraftAttachments: (sessionId) =>
    set((state) => clearDrafts(state, sessionId)),
  setPendingProgress: (sessionId, localId, progress) =>
    set((state) => setProgress(state, sessionId, localId, progress)),
  applyServerMessage: (message) =>
    set((state) => applyServerMessageToState(state, message)),
  clearOutput: (sessionId) =>
    set((state) => clearSessionOutput(state, sessionId)),
  pushHistory: (sessionId, text) =>
    set((state) => appendHistory(state, sessionId, text)),
  addPendingInput: (sessionId, input) =>
    set((state) => addPending(state, sessionId, input)),
  setPendingStatus: (sessionId, localId, status) =>
    set((state) => setPending(state, sessionId, localId, status)),
  dismissPendingInput: (sessionId, localId) =>
    set((state) => removePending(state, sessionId, localId)),
  dismissSuggestion: (sessionId, entryId) =>
    set((state) => markSuggestionDismissed(state, sessionId, entryId)),
  renameSession: (sessionId, name) =>
    set((state) => renameSessionInState(state, sessionId, name)),
  removeSession: (sessionId) =>
    set((state) => removeSessionFromState(state, sessionId)),
  reset: () => {
    cancelHighlightTimer();
    set(INITIAL_TERMINAL_STATE);
  },
});
